import React, {ReactNode} from 'react'
import {findDOMNode} from 'react-dom'

import SelectProgressPopover from "./components/popover"

import FormManager from '../../gd-form/common/form-manager'
import HintPopover from "../hint-popover/hint-popover"
import ControlButtonStandard from "../../gd-components/buttons/control-button/control-button-standard"
import {PopoverAttachmentType} from "../../gd-components/gd-popover/@const"

type Props = {
    className?          : string

    value?              : number
    valueLabel?         : string | ReactNode          // string value to override percents label
    valueLabelPosition? : 'right' | 'left' | 'none'
    placeholder?        : string
    decimalPlaces?      : number

    hint?               : string
    notSetPlaceholder?  : string

    // underline
    underlined?         : 'on-hover' | 'always' | 'never'  // if set will show border bottom for the value (for text-XX renderValue only)

    // look and feel
    renderValue         : 'bar-100' | 'bar-50' | 'text-13' | 'text-12' | 'text-11' | 'inline' | 'control-button' | 'goal-100' | 'goal-80' | 'func' | 'thin-line-100' // render value view name
    renderValueFunc?    : any               // func for external render function if passed used to render instead of default
    iconSize?           : number // fs-X or default 14px if unset (currently for 'control-button' only)

    name?               : string
    form?               : FormManager

    deleteButton?       : boolean           // if true will show delete button to reset date to null
    changeToGrayOnDone? : boolean
    disabled?           : boolean

    // popover options
    attachment?         : string,
    targetAttachment?   : string,
    offset?             : string,
    attachmentType?     : PopoverAttachmentType | 'full' | 'vertical' | 'horizontal',

    // Callbacks
    onUserSelect?       : callback,
    onTogglePopover?    : callback                  // to track if any popover opened from this component
}

export default class SelectProgress extends React.Component<Props> {

    form: FormManager

    static defaultProps = {
        valueLabelPosition  : 'right',
        placeholder         : "Progress",
        decimalPlaces       : 2,
        deleteButton        : false,
        attachment          : "bottom center",
        targetAttachment    : "top center",
        attachmentType      : 'horizontal'
    }

    state = {
        showPopover: false,
        progress: this.props.value
    }

    constructor(props:Props) {
        super(props)

        const initFormData = {
            progress: this.currentValue
        }

        const formStructure = {
            fields: {
                progress: {type: 'select', isRequired: false}
            },
            onSubmit: this.onSubmit
        }

        this.form = new FormManager(formStructure,initFormData)
    }

    componentDidMount() {
        this.form.formChanged()
    }

    // when used as a form element the form is the source of truth, otherwise the value prop is
    get currentValue(): number | null | undefined {
        return this.props.form && this.props.name
            ? this.props.form.getValue(this.props.name)
            : this.props.value
    }

    showPopover = () => {
        if (!this.props.disabled && !this.state.showPopover) {
            this.setState({
                showPopover: true
            }, ()=>{
                if (this.props.onTogglePopover) this.props.onTogglePopover(true)
            })
        }
    }

    hidePopover = (callback?:any) => {
        if (this.state.showPopover){
            this.setState({
                showPopover: false
            }, ()=>{
                this.form.reset(true)
                if (this.props.onTogglePopover) this.props.onTogglePopover(false)
                if(callback) callback()
            })
        }
    }

    onClickOutside = () => {
        this.form.setValue("progress", this.currentValue)
        this.setState({
            progress: this.currentValue
        }, ()=>{
            this.hidePopover()
        })
    }

    onDeleteClick = (e:any) => {
        if (e) e.stopPropagation()

        if (this.state.showPopover)
            this.hidePopover(()=>{
                if (this.props.onUserSelect) this.props.onUserSelect(null)
            })
        else if (this.props.onUserSelect) this.props.onUserSelect(null)
    }

    onProgressChange = (value:number) => {
        const progress = value > 100 ? 100 : value < 0 ? 0 : value

        if (this.props.name && this.props.form) this.props.form.onFormComponentValueChange(this.props.name, progress)

        this.form.setValue("progress", progress)
        this.setState({
            progress: progress
        })
    }

    onSubmit = (result:any) => {
        this.hidePopover(()=>{
            if (this.props.onUserSelect) this.props.onUserSelect(result.progress)
        })
    }

    onSelectorItemClick = (value:number) => {
        if (this.props.name && this.props.form) this.props.form.onFormComponentValueChange(this.props.name, value)

        this.form.onFormComponentValueChange("progress", value)
        this.hidePopover(()=>{
            if (this.props.onUserSelect) this.props.onUserSelect(value)
        })
    }

    renderPopover() {
        if (this.state.showPopover) {
            return <SelectProgressPopover form={this.form}
                                          attachment={this.props.attachment}
                                          targetAttachment={this.props.targetAttachment}
                                          attachmentType={this.props.attachmentType}
                                          offset={this.props.offset}
                                          getTargetFunc={()=>findDOMNode(this)}
                                          onClickOutside={this.onClickOutside}
                                          onSelectorItemClick={this.onSelectorItemClick}
                                          onProgressChange={this.onProgressChange} />
        }
        return null
    }

    renderValue = () => {
        let res

        switch (this.props.renderValue) {
            case 'bar-100':
            case 'bar-50':
            case 'goal-80':
            case 'goal-100':
            case 'thin-line-100':
                const notSet    = _.isNil(this.props.value)
                const excess    = this.props.value ? this.props.value > 100 : false
                const value     = this.props.value && excess ? Math.floor(100 * (100 / this.props.value)) : this.props.value

                const classes = Classes("ui5-progress", this.props.renderValue, /*this.props.className,*/ {
                    'excess-v2'     : excess,
                    'disabled'      : this.props.disabled,
                    'show-popover'  : this.state.showPopover,
                    'not-set'       : notSet
                })

                if (this.props.notSetPlaceholder && notSet) {
                    res = (
                        <div className={classes} onClick={this.showPopover}>
                            <div className="no-progress ui5-txt-default-small">{this.props.notSetPlaceholder}</div>
                        </div>
                    )
                } else {

                    let labelLeft
                    let labelRight
                    if (!_.isNil(value) && this.props.valueLabelPosition !== 'none') {
                        let valueLabel: any = !!this.props.valueLabel ? this.props.valueLabel : !_.isNil(this.props.value) ? "{0}%".f(parseFloat(this.props.value.toFixed(this.props.decimalPlaces))) : ''

                        switch (this.props.renderValue) {
                            case 'goal-100':
                                valueLabel = <div className="ui5-property">{valueLabel}</div>
                                break

                            case 'goal-80':
                                valueLabel = <div className="ui5-txt-not-important-small m-l-15">{valueLabel}</div>
                        }

                        const labelClasses = Classes("progress-label ui5-txt-less-important-small percentage", {
                            'align-right': this.props.valueLabelPosition === 'left'
                        })
                        const label = <div className={labelClasses}>{valueLabel}</div>

                        switch (this.props.valueLabelPosition) {
                            case 'left':
                                labelLeft = label
                                break

                            case 'right':
                                labelRight = label
                                break
                        }
                    }

                    res = (
                        <div className={classes} onClick={this.showPopover}>
                            {labelLeft}

                            <div className="progress-bg">
                                {!!value &&
                                    <div className  = {Classes("progress-line", {'full': value === 100})}
                                         style      = {{width: '{0}%'.f(value)}}
                                    />}
                            </div>

                            {labelRight}

                            {this.props.deleteButton &&
                                <div className  = "progress-reset ui5-ico-delete s14 gray"
                                     onClick    = {this.onDeleteClick}
                                />}
                        </div>
                    )
                }

                break

            case 'inline': {
                const value = this.currentValue
                const notSet = _.isNil(value)

                const curValueClasses = Classes('ui5-circle-selector', {
                    'no-value': notSet
                })

                res = (
                    <div className={curValueClasses} onClick={this.showPopover}>
                        <div className="icon i5-percent fs-13"/>
                        {!notSet && <div className="ui5-txt-no-accent-small">{"{0}%".f(value)}</div>}
                    </div>
                )
            } break

            case 'text-13':
            case 'text-12':
            case 'text-11': {
                const value = this.currentValue;

                const valueLabel = !!this.props.valueLabel ?
                                                                      this.props.valueLabel :
                                                                      !_.isNil(value) ?
                                                                      "{0}%".f(parseFloat(value.toFixed(this.props.decimalPlaces))) :
                                                                      this.props.placeholder;

                const underlined = this.props.disabled ? 'underlined-never' : 'underlined-' + this.props.underlined;

                const curValueClasses = Classes('cur-value', this.props.renderValue, underlined, "m-t-10", "m-l-5", {
                    'no-value': _.isNil(value)
                })

                res = (
                    <>
                        <div className={curValueClasses} onClick={this.showPopover}>{valueLabel}</div>
                        {this.props.deleteButton && <div className="i5-delete-circle-solid" onClick={this.onDeleteClick}/>}
                    </>
                )
            } break

            case 'control-button':
                const iconClasses = Classes("i5-percent", {
                    ['fs-'+this.props.iconSize]: !!this.props.iconSize
                })

                res = (
                    <ControlButtonStandard
                        iconClass   = {iconClasses}
                        isDisabled  = {this.props.disabled}
                        onClick     = {this.showPopover}
                    />
                )
              break
        }

        return res
    }

    render() {
        const value  = this.currentValue
        const isDone = value && value >= 100
        const classes = Classes("gd-select-progress",  this.props.className, 'render-'+this.props.renderValue, {
            'is-done-gray'  : isDone && this.props.changeToGrayOnDone,
            disabled        : this.props.disabled,
            'not-set'       : _.isNil(value),
            'popup-open'    : this.state.showPopover
        })

        return (
            <div className={classes}>
                {this.renderValue()}
                {this.renderPopover()}
                {this.props.hint && !this.state.showPopover && <HintPopover hint={this.props.hint}/>}
            </div>
        )
    }
}
